import { isSameCoffee } from "@bcc/components/products/coffee-identity";
import { REJECTED_STATUS } from "@bcc/components/products/products-table/products-table.utils";
import type { SalesforceSkuApprovalStatus } from "@shared/services/salesforce/types";
import type { FormProduct, FormVariant, Product, ProductConnection, Variant } from "@shared/types";
import { isIntensityPosition } from "@shared/types/intensity";

/**
 * Salesforce `Approval_status__c` values that mark a row as an in-progress
 * onboarding record rather than an approved catalog entry. The filter checks
 * `status` directly rather than routing through `resolveWebStatus`, because
 * that helper also returns "Pending" for variants with no status at all —
 * which would mis-classify plain installation (Shopify) variants that must
 * remain visible.
 *
 * "Rejected" is intentionally excluded: a manager rejection is an explicit
 * action that the vendor must be able to see. Rejected variants without a
 * PIM webStatus surface in the products table with a red "Rejected" badge
 * via resolveWebStatus.
 */
const HIDDEN_SALESFORCE_STATUSES: ReadonlySet<SalesforceSkuApprovalStatus> = new Set<SalesforceSkuApprovalStatus>([
  "Draft",
  "Submitted for Approval",
  "Copywrite incomplete",
  "Copywrite / Content submitted",
  "Copywrite / Content approved",
]);

/**
 * Narrows an arbitrary string to the hidden-statuses subset. `variant.status`
 * is typed as `string | undefined` upstream, so we can't hand it to
 * `ReadonlySet<SalesforceSkuApprovalStatus>.has` directly — the set exists
 * to catch typos, not to accept any string. This guard localises the cast.
 */
const isHiddenSalesforceStatus = (status: string): status is SalesforceSkuApprovalStatus =>
  HIDDEN_SALESFORCE_STATUSES.has(status as SalesforceSkuApprovalStatus);

/**
 * Converts a raw Product (from PIM or Salesforce) into a FormProduct
 * with minimal transformation — just enough for the products table.
 */
export function productToFormProduct(product: Product): FormProduct {
  const setResolvedVariants = (variants: FormVariant[] | undefined): FormVariant[] => {
    if (!variants || variants.length === 0) {
      return [
        {
          type: "variant" as const,
          id: product.id ?? product.serviceId ?? "",
          bagSize: "",
          grind: product.grind ?? [],
          status: product.status,
        } satisfies FormVariant,
      ];
    }
    return variants;
  };

  const mappedVariants: FormVariant[] | undefined = product.variants?.map((variant) => ({
    ...variant,
    bagSize: variant.bagSize ?? "",
  }));

  const resolvedVariants = setResolvedVariants(mappedVariants);

  return {
    ...product,
    id: product.serviceId ?? product.id ?? "",
    productType: product.productType ?? "",
    isProductFormValid: false,
    variants: resolvedVariants,
  };
}

/**
 * Builds a lookup map of PIM products keyed by both serviceId and id.
 */
function buildPimLookup(pimProducts: Product[]): Map<string, Product> {
  const lookup = new Map<string, Product>();
  for (const pim of pimProducts) {
    if (pim.serviceId) lookup.set(pim.serviceId, pim);
    if (pim.id) lookup.set(pim.id, pim);
  }
  return lookup;
}

/**
 * Overlays matching PIM per-variant fields (webStatus, price, wholesalePrice,
 * webPrice, weeklyInventory) onto each of the product's variants.
 */
function enrichVariantsWithPimData(variants: FormVariant[], pimVariants: readonly Variant[]): void {
  for (const variant of variants) {
    const pimVariant = pimVariants.find((pimVariant) => pimVariant.id === variant.id);
    if (pimVariant?.webStatus) {
      variant.webStatus = pimVariant.webStatus;
    }
    if (pimVariant?.price !== undefined) {
      variant.price = pimVariant.price;
    }
    if (pimVariant?.wholesalePrice !== undefined) {
      variant.wholesalePrice = pimVariant.wholesalePrice;
    }
    if (pimVariant?.webPrice !== undefined) {
      variant.webPrice = pimVariant.webPrice;
    }
    if (pimVariant?.weeklyInventory !== undefined) {
      variant.weeklyInventory = pimVariant.weeklyInventory;
    }
  }
}

/**
 * Once PIM has approved a position for this coffee, that value — and the
 * lock it implies — must win over the Salesforce-primary `formProduct`'s
 * still-provisional one. Without this, a rejected bag-size request under an
 * already-approved parent reopens with the provisional score editable and
 * can overwrite the approved product-level value.
 */
function promotePimApprovedIntensity(formProduct: FormProduct, pimMatch: Product): void {
  if (!isIntensityPosition(pimMatch.intensityPosition)) return;
  if (pimMatch.intensityPosition !== formProduct.intensityPosition) {
    formProduct.closestMatchCoffee = undefined;
  }
  formProduct.intensityPosition = pimMatch.intensityPosition;
  formProduct.intensityZone = pimMatch.intensityZone;
  formProduct.hasApprovedIntensity = true;
}

/**
 * Salesforce statuses that have not (yet) been through Beanz Manager approval:
 * every in-progress onboarding state (Draft, Submitted, the Copywrite stages —
 * all "Pending" to the vendor) plus Rejected. A record in one of these states
 * cannot establish a score for its siblings: two simultaneously pending
 * requests for the same coffee would otherwise lock each other, and a first
 * submission would lose its own editable score.
 */
const NON_ESTABLISHING_STATUSES: ReadonlySet<string> = new Set([...HIDDEN_SALESFORCE_STATUSES, REJECTED_STATUS]);

// Any "Copywrite…" spelling counts as pending, including the legacy
// "Copywrite-Pending" that `resolveWebStatus` still maps to Pending.
const hasEstablishingStatus = (status: string | undefined): boolean =>
  !status || (!NON_ESTABLISHING_STATUSES.has(status) && !status.startsWith("Copywrite"));

const hasEstablishedPosition = (product: FormProduct): boolean =>
  isIntensityPosition(product.intensityPosition) && (product.hasApprovedIntensity === true || hasEstablishingStatus(product.status));

interface SiblingCandidate {
  original: FormProduct;
  /** Frozen before any promotion, so a record promoted earlier in the pass cannot pass on a value it only inherited. */
  view: FormProduct;
}

/**
 * Candidates able to establish a score, strongest first: every PIM-approved
 * position (PIM products — matched or not, so a renamed PIM title still
 * links a later request — and Salesforce records PIM already scored), then
 * Salesforce records approved by the beans manager whose score PIM has not
 * picked up yet.
 */
function buildSiblingCandidates(merged: readonly FormProduct[], pimProducts: readonly Product[]): SiblingCandidate[] {
  const toCandidate = (original: FormProduct): SiblingCandidate => ({ original, view: { ...original } });
  const pimApproved = pimProducts.map((pim) => toCandidate(productToFormProduct(pim)));
  // Salesforce returns no creation timestamp, and its response order is not
  // stable. Record Ids increase with creation, so among equally ranked records
  // the lowest Id — the earliest request — establishes the score.
  const earliestFirst = (left: SiblingCandidate, right: SiblingCandidate): number =>
    (left.original.serviceId ?? left.original.id).localeCompare(right.original.serviceId ?? right.original.id);
  const sfApproved = merged
    .filter((product) => product.hasApprovedIntensity)
    .map(toCandidate)
    .sort(earliestFirst);
  const sfProvisional = merged
    .filter((product) => !product.hasApprovedIntensity)
    .map(toCandidate)
    .sort(earliestFirst);
  return [...pimApproved, ...sfApproved, ...sfProvisional].filter((candidate) => hasEstablishedPosition(candidate.view));
}

/**
 * A coffee not yet in PIM can still have its score established by an earlier,
 * already-approved request for another grind / bag size of the same coffee —
 * or by the PIM product its (possibly renamed) title was seeded from. That
 * sibling's position and "most similar to" pick win, and the product locks
 * like a PIM-approved one so a later grind never re-submits (and the beans
 * manager never re-edits) the intensity. A sibling that carries no pick (PIM
 * keeps none) leaves this record's own pick alone when the position agrees.
 */
function promoteSiblingEstablishedIntensity(formProduct: FormProduct, candidates: readonly SiblingCandidate[]): void {
  // The record itself may be a candidate (an approved Salesforce record PIM has
  // not scored): every record of the same coffee then resolves to the same
  // highest-priority candidate, so approved siblings never swap scores.
  const match = candidates.find((candidate) => isSameCoffee(candidate.view, formProduct));
  if (!match || match.original === formProduct) return;
  const sibling = match.view;
  const samePosition = sibling.intensityPosition === formProduct.intensityPosition;
  // PIM keeps no pick, so recover it from an established sibling at that position.
  const siblingPick =
    sibling.closestMatchCoffee ??
    candidates.find(
      (candidate) =>
        isSameCoffee(candidate.view, formProduct) &&
        candidate.view.intensityPosition === sibling.intensityPosition &&
        Boolean(candidate.view.closestMatchCoffee)
    )?.view.closestMatchCoffee;
  formProduct.closestMatchCoffee = siblingPick ?? (samePosition ? formProduct.closestMatchCoffee : undefined);
  formProduct.intensityPosition = sibling.intensityPosition;
  formProduct.intensityZone = sibling.intensityZone;
  formProduct.hasApprovedIntensity = true;
}

/**
 * PIM keeps the approved position but not the roaster's "most similar to"
 * pick, and lags Salesforce when the coffee was only just approved. So a
 * coffee picked from the PIM-only list for a new bag size would send no pick,
 * or show a locked, empty section. Fill both from the approved Salesforce
 * record of the same coffee: an unscored PIM coffee takes its position, zone
 * and pick; a scored one takes the pick when the positions agree.
 */
export function withSalesforceIntensity(pimProducts: FormProduct[], salesforceProducts: readonly Product[]): FormProduct[] {
  return pimProducts.map((pim) => {
    const isScored = isIntensityPosition(pim.intensityPosition);
    if (isScored && pim.closestMatchCoffee) return pim;
    const match = salesforceProducts.find(
      (sf) =>
        isIntensityPosition(sf.intensityPosition) &&
        hasEstablishingStatus(sf.status) &&
        (isScored ? Boolean(sf.closestMatchCoffee) && sf.intensityPosition === pim.intensityPosition : true) &&
        isSameCoffee(sf, pim)
    );
    if (!match) return pim;
    return isScored
      ? { ...pim, closestMatchCoffee: match.closestMatchCoffee }
      : {
          ...pim,
          intensityPosition: match.intensityPosition,
          intensityZone: match.intensityZone,
          closestMatchCoffee: match.closestMatchCoffee,
        };
  });
}

/**
 * Enriches a FormProduct with matching PIM data.
 */
function enrichWithPimData(formProduct: FormProduct, pimMatch: Product): void {
  if (pimMatch.images?.length) {
    formProduct.images = pimMatch.images;
  }
  if (pimMatch.description) {
    formProduct.description = pimMatch.description;
  }
  if (pimMatch.roasterNotes) {
    formProduct.roasterNotes = pimMatch.roasterNotes;
  }
  // Carry the PIM record's localised copy onto the merged product. PIM is the
  // source of truth for a live SKU's per-locale description/roaster notes, and
  // the "Add New Bag Size" flow reads these to pre-fill the copy-editing
  // fields sent to Beanz Manager approval.
  if (pimMatch.localizedFields) {
    formProduct.localizedFields = pimMatch.localizedFields;
  }

  promotePimApprovedIntensity(formProduct, pimMatch);
  enrichVariantsWithPimData(formProduct.variants, pimMatch.variants ?? []);
}

/**
 * Carries the Salesforce status onto variants that don't already have one.
 */
function applyFallbackStatus(formProduct: FormProduct, sfStatus: string | undefined): void {
  if (!sfStatus) return;
  for (const variant of formProduct.variants) {
    if (!variant.status) {
      variant.status = sfStatus;
    }
  }
}

/**
 * Merges Salesforce products with PIM products for SS roasters.
 *
 * - Salesforce products are the primary list (all submitted SKUs with approval status)
 * - PIM products enrich with webStatus, images, description, etc.
 * - PIM-only products (not in Salesforce) are also included
 */
export function mergeSalesforceAndPimProducts(salesforceProducts: Product[], pimProducts: Product[]): FormProduct[] {
  const pimByServiceId = buildPimLookup(pimProducts);
  const seenPimIds = new Set<string>();
  const merged: FormProduct[] = [];

  for (const sfProduct of salesforceProducts) {
    const formProduct = productToFormProduct(sfProduct);

    const pimMatch = pimByServiceId.get(sfProduct.serviceId ?? "") ?? pimByServiceId.get(sfProduct.id ?? "");

    if (pimMatch) {
      if (pimMatch.serviceId) seenPimIds.add(pimMatch.serviceId);
      if (pimMatch.id) seenPimIds.add(pimMatch.id);
      enrichWithPimData(formProduct, pimMatch);
    }
    applyFallbackStatus(formProduct, sfProduct.status);
    merged.push(formProduct);
  }

  // Include PIM-only products (not in Salesforce)
  const pimOnly: FormProduct[] = [];
  for (const pim of pimProducts) {
    const id = pim.serviceId ?? pim.id ?? "";
    if (!seenPimIds.has(id)) {
      pimOnly.push(productToFormProduct(pim));
    }
  }

  // Second pass, once every Salesforce record carries its PIM data: a record
  // PIM has not scored may inherit the score of an established sibling.
  const candidates = buildSiblingCandidates(merged, pimProducts);
  for (const formProduct of merged) {
    if (!formProduct.hasApprovedIntensity) {
      promoteSiblingEstablishedIntensity(formProduct, candidates);
    }
  }
  merged.push(...pimOnly);

  return merged;
}

export interface PimFirstOptions {
  salesforceProducts: Product[];
  connections: ProductConnection[];
  remoteProducts: Product[];
}

/**
 * Builds the PIM-first FormProduct list for the Standard BCC products table.
 *
 * - Keeps every PIM variant from the catalog so the Products table reflects
 *   the full PIM product list. In this helper, connection state only decides
 *   whether ACTIVE Salesforce/live-inventory overlays are applied; PAUSED
 *   remap styling is handled downstream by table/rendering logic.
 * - Keeps each surviving PIM variant's `id` as the PIM SKU (`sourceObjectId`)
 *   so downstream logic can continue to identify the original PIM variant.
 *   No id translation. No `targetVariantId` remap.
 * - For connected variants, overlays Salesforce approval status and fallback
 *   pricing by walking `connection.targetVariantId -> sfVariant`. Salesforce
 *   variants are keyed by Shopify `targetVariantId` (see
 *   `shared/src/lib/sanitize/products.ts` `mapSalesforceSkuImportVariant`),
 *   not PIM SKU, so the connection is the join. PIM `webStatus`, pricing, and
 *   `weeklyInventory` always win; SF never overwrites them. Standard table
 *   inventory displays use the PIM `inventoryQuantity` when present, so that
 *   approved PIM inventory updates are not hidden by stale Salesforce
 *   `maxBagsShippedPerWeek` values.
 * - For ACTIVE connections, overlays live installation inventory by walking
 *   `connection.targetVariantId` into `remoteProducts` and copying
 *   `inventoryQuantity` onto the row. When a PIM SKU has multiple connections,
 *   their remote `inventoryQuantity` values are summed so the row reflects all
 *   stock mapped to that PIM variant.
 * - `weeklyInventory` is sourced from PIM `inventoryQuantity` when present,
 *   then PIM `weeklyInventory`, then Salesforce (`maxBagsShippedPerWeek`), and
 *   never from remote stock.
 */
export function buildPimFirstFormProducts(pimProducts: Product[], options: PimFirstOptions): FormProduct[] {
  const { salesforceProducts, connections, remoteProducts } = options;

  const connectionsByPimSku = indexConnectionsByPimSku(connections);
  const remoteVariantById = indexRemoteVariants(remoteProducts);
  const sfVariantByTargetId = indexSalesforceVariantsByTargetId(salesforceProducts);
  const shipToByItemNumber = indexShipToByItemNumber(salesforceProducts);

  const results: FormProduct[] = [];
  const consumedPimSkus = new Set<string>();
  for (const pimProduct of pimProducts) {
    const { survivors: survivingVariants, shipTo } = collectSurvivingVariants(pimProduct, {
      connectionsByPimSku,
      remoteVariantById,
      sfVariantByTargetId,
      shipToByItemNumber,
    });
    for (const variant of survivingVariants) {
      if (variant.id) consumedPimSkus.add(variant.id);
    }
    if (survivingVariants.length === 0) continue;
    results.push({
      ...pimProduct,
      id: pimProduct.id ?? pimProduct.serviceId ?? "",
      productType: pimProduct.productType ?? "",
      isProductFormValid: false,
      variants: survivingVariants,
      // Carry the Salesforce product-level ship-to (e.g. "NL;DE") onto the
      // PIM-first FormProduct so the cross-border "Ship to" column can render.
      // PIM has no ship-to, so it comes from the SF product whose `item_numbers`
      // matches one of this product's variant ids (see collectSurvivingVariants);
      // falls back to any pre-existing PIM value when no SF match exists.
      ship_to: shipTo ?? pimProduct.ship_to,
    });
  }

  // Concat orphan rows for connections whose `sourceObjectId` did not match
  // any PIM variant (e.g. a PIM product whose `variants` is empty but that
  // still carries a PAUSED connection from an earlier mapping). Mirrors the
  // client's `ProductConnectionTableV2` pattern of feeding two sources into
  // the table so disconnected rows remain visible for remap.
  const orphanForms = buildOrphanedConnectionFormProducts(
    connections,
    consumedPimSkus,
    remoteProducts,
    sfVariantByTargetId,
    shipToByItemNumber
  );
  results.push(...orphanForms);

  return results;
}

/**
 * Synthesises `FormProduct`s for ACTIVE or PAUSED connections whose
 * `sourceObjectId` does not match any PIM variant in the current PIM feed.
 * This covers the case where a PIM product has an empty `variants` array but
 * a connection still exists (e.g. after a disconnect, or when PIM has been
 * restructured under an existing mapping) — without this, the row would
 * disappear from the table and the user would have no way to re-map it.
 *
 * Orphaned connections are grouped by `targetProductId` so every synthetic
 * `FormProduct` collects the variants that all map to the same remote
 * product. When a matching remote product is available, its `title` is used
 * for the product; otherwise the first connection's `identifier` is a safe
 * fallback. Each synthetic `FormVariant` keeps its `id` as the PIM
 * `sourceObjectId` so the existing PAUSED-detection logic
 * (`pausedVariantIds` in products-table.tsx) stamps `tone: "subdued"`.
 */
/** Groups orphaned connections (ACTIVE/PAUSED, unconsumed by PIM) by their target product. */
const groupOrphanedConnectionsByProduct = (
  connections: ProductConnection[],
  consumedPimSkus: Set<string>
): Map<string, ProductConnection[]> => {
  const groups = new Map<string, ProductConnection[]>();
  for (const connection of connections) {
    if (connection.state !== "ACTIVE" && connection.state !== "PAUSED") continue;
    if (!connection.sourceObjectId) continue;
    if (consumedPimSkus.has(connection.sourceObjectId)) continue;
    const key = connection.targetProductId || connection.sourceObjectId;
    const existing = groups.get(key);
    if (existing) {
      existing.push(connection);
    } else {
      groups.set(key, [connection]);
    }
  }
  return groups;
};

const indexProductsById = (products: Product[]): Map<string, Product> => {
  const byId = new Map<string, Product>();
  for (const product of products) {
    if (product.id) byId.set(product.id, product);
  }
  return byId;
};

/**
 * Builds one synthetic `FormVariant` for an orphaned connection, plus the
 * ship-to value it contributes (if any). ship-to is joined to PIM by matching
 * the Salesforce `item_numbers` against the orphan row's PIM SKU
 * (`sourceObjectId`).
 */
const buildOrphanFormVariant = (
  connection: ProductConnection,
  productId: string,
  sfVariantByTargetId: Map<string, SalesforceMatch>,
  shipToByItemNumber: Map<string, string>
): { variant: FormVariant; shipTo: string | undefined } => {
  const salesforceMatch = findSalesforceMatch([connection], sfVariantByTargetId);
  const variant: FormVariant = {
    type: "variant",
    id: connection.sourceObjectId,
    productId,
    title: connection.identifier,
    bagSize: connection.bagSize ?? "",
    grind: connection.grindType ? [connection.grindType] : undefined,
    // Only flag as Shopify-backed when the connection is ACTIVE — mirrors
    // applyInventoryOverlay's ACTIVE-only rule so PAUSED orphan rows don't
    // surface a "Shopify" source badge that non-orphan PAUSED rows lack.
    isImportedLocalVariant: connection.state === "ACTIVE" && Boolean(connection.targetVariantId),
  };
  applySalesforceOverlay(variant, salesforceMatch);

  const shipTo = connection.sourceObjectId ? shipToByItemNumber.get(connection.sourceObjectId.toUpperCase()) : undefined;

  return { variant, shipTo };
};

/** Builds a synthetic `FormProduct` from a group of orphaned connections that share a target product. */
const buildOrphanFormProduct = (
  key: string,
  first: ProductConnection,
  group: ProductConnection[],
  remoteById: Map<string, Product>,
  sfVariantByTargetId: Map<string, SalesforceMatch>,
  shipToByItemNumber: Map<string, string>
): FormProduct => {
  const remote = remoteById.get(first.targetProductId);
  const productTitle = remote?.title ?? first.identifier ?? "";

  // Capture the first non-empty ship-to value across the grouped connections.
  let shipTo: string | undefined;
  const variants: FormVariant[] = group.map((connection) => {
    const result = buildOrphanFormVariant(connection, key, sfVariantByTargetId, shipToByItemNumber);
    shipTo ??= result.shipTo;
    return result.variant;
  });

  return {
    type: "product",
    id: key,
    title: productTitle,
    productType: "",
    isProductFormValid: false,
    variants,
    ...(shipTo ? { ship_to: shipTo } : {}),
  };
};

const buildOrphanedConnectionFormProducts = (
  connections: ProductConnection[],
  consumedPimSkus: Set<string>,
  remoteProducts: Product[],
  sfVariantByTargetId: Map<string, SalesforceMatch>,
  shipToByItemNumber: Map<string, string>
): FormProduct[] => {
  const groups = groupOrphanedConnectionsByProduct(connections, consumedPimSkus);
  if (groups.size === 0) return [];

  const remoteById = indexProductsById(remoteProducts);

  const results: FormProduct[] = [];
  for (const [key, group] of groups) {
    const first = group[0];
    if (!first) continue;
    results.push(buildOrphanFormProduct(key, first, group, remoteById, sfVariantByTargetId, shipToByItemNumber));
  }
  return results;
};

const indexConnectionsByPimSku = (connections: ProductConnection[]): Map<string, ProductConnection[]> => {
  const map = new Map<string, ProductConnection[]>();
  for (const connection of connections) {
    if (!connection.sourceObjectId) continue;
    // ACTIVE and PAUSED both surface rows. PAUSED rows are rendered
    // greyed (via `tone: "subdued"`) and offered for remap through
    // the per-row map-product action. DELETED and any other states
    // remain excluded.
    if (connection.state !== "ACTIVE" && connection.state !== "PAUSED") {
      continue;
    }
    const existing = map.get(connection.sourceObjectId);
    if (existing) {
      existing.push(connection);
    } else {
      map.set(connection.sourceObjectId, [connection]);
    }
  }
  return map;
};

const indexRemoteVariants = (remoteProducts: Product[]): Map<string, Variant> => {
  const map = new Map<string, Variant>();
  for (const product of remoteProducts) {
    for (const variant of product.variants ?? []) {
      if (variant.id) map.set(variant.id, variant);
    }
  }
  return map;
};

interface SalesforceMatch {
  variant: Variant;
  product: Product;
}

/**
 * Index Salesforce variants by their Shopify `targetVariantId` (the SF
 * sanitizer in `shared/src/lib/sanitize/products.ts:275` sets
 * `variant.id = targetVariantId`). PIM SKUs reach SF through the connection
 * join, not a direct variant-id match.
 */
const indexSalesforceVariantsByTargetId = (salesforceProducts: Product[]): Map<string, SalesforceMatch> => {
  const map = new Map<string, SalesforceMatch>();
  for (const product of salesforceProducts) {
    for (const variant of product.variants ?? []) {
      if (variant.id) map.set(variant.id, { variant, product });
    }
  }
  return map;
};

/**
 * Builds a lookup from PIM SKU (item number) to the Salesforce product-level
 * `ship_to` value. Salesforce records the connected PIM SKU(s) on each product
 * in `item_numbers` (`Item_numbers__c`), which may be a ","/";"-delimited list.
 * PIM variant ids are upper-cased SKU codes, so each segment is trimmed and
 * upper-cased to match. The first non-empty `ship_to` wins when a SKU appears
 * in more than one Salesforce product. Products missing either field are
 * skipped, so the map only holds SKUs that actually carry a ship-to.
 */
const indexShipToByItemNumber = (salesforceProducts: Product[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const product of salesforceProducts) {
    if (!product.item_numbers || !product.ship_to) continue;
    for (const segment of product.item_numbers.split(/[,;]+/)) {
      const sku = segment.trim().toUpperCase();
      if (sku && !map.has(sku)) {
        map.set(sku, product.ship_to);
      }
    }
  }
  return map;
};

interface CollectContext {
  connectionsByPimSku: Map<string, ProductConnection[]>;
  remoteVariantById: Map<string, Variant>;
  sfVariantByTargetId: Map<string, SalesforceMatch>;
  // PIM SKU → Salesforce product-level ship-to, keyed by the SF `item_numbers`.
  shipToByItemNumber: Map<string, string>;
}

interface SurvivingVariantsResult {
  survivors: FormVariant[];
  // Salesforce product-level ship-to (e.g. "NL;DE"), resolved by matching the
  // SF `item_numbers` against the PIM variant id (SKU) — see
  // `indexShipToByItemNumber`. The first non-empty value across the product's
  // variants wins. PIM carries no ship-to, so this is the only source.
  // Undefined for non-cross-border products.
  shipTo?: string | null;
}

const collectSurvivingVariants = (pimProduct: Product, context: CollectContext): SurvivingVariantsResult => {
  const survivors: FormVariant[] = [];
  let shipTo: string | null | undefined;
  for (const pimVariant of pimProduct.variants ?? []) {
    if (!pimVariant.id) continue;
    const relatedConnections = context.connectionsByPimSku.get(pimVariant.id) ?? [];
    // Live inventory and source badge are ACTIVE-only: a PAUSED connection
    // surfaces the row for remap but is disconnected from Shopify stock.
    // Unconnected PIM variants still surface as catalog rows without
    // connection-backed overlays. Salesforce catalog metadata still applies to
    // PAUSED rows as fallback so price, weekly limit, and expected
    // out-of-stock dates do not disappear after disconnect.
    const activeConnections = relatedConnections.filter((connection) => connection.state === "ACTIVE");
    const salesforceMatch =
      findSalesforceMatch(activeConnections, context.sfVariantByTargetId) ??
      findSalesforceMatch(relatedConnections, context.sfVariantByTargetId);
    const formVariant: FormVariant = {
      ...pimVariant,
      bagSize: pimVariant.bagSize ?? "",
    };
    applyPimInventoryPrecedence(formVariant);
    applyConnectionGrindFallback(formVariant, activeConnections);
    applySalesforceOverlay(formVariant, salesforceMatch);
    if (activeConnections.length > 0) {
      applyInventoryOverlay(formVariant, activeConnections, context.remoteVariantById);
    }
    // ship-to is a Salesforce product-level field, joined to PIM by matching
    // the SF `item_numbers` against this variant's id (SKU). Capture the first
    // non-empty value found among the product's variants.
    if (!shipTo) {
      const variantShipTo = context.shipToByItemNumber.get(pimVariant.id.toUpperCase());
      if (variantShipTo) shipTo = variantShipTo;
    }
    survivors.push(formVariant);
  }
  return { survivors, shipTo };
};

const applyPimInventoryPrecedence = (target: FormVariant): void => {
  if (typeof target.inventoryQuantity === "number") {
    target.weeklyInventory = target.inventoryQuantity;
  }
};

const applyConnectionGrindFallback = (target: FormVariant, connections: ProductConnection[]): void => {
  if (target.grind && target.grind.length > 0) return;
  const grinds = [...new Set(connections.map((connection) => connection.grindType).filter((grindType) => grindType.length > 0))];
  if (grinds.length > 0) {
    target.grind = grinds;
  }
};

/**
 * Walks the PIM variant's connections in order and returns the first SF match.
 * Multi-connection SKUs with divergent SF records are rare in practice; when
 * they do occur, the primary connection wins (same ordering as the inventory
 * overlay). If no connection has a SF record, returns undefined and the
 * overlay is skipped.
 */
const findSalesforceMatch = (
  connections: ProductConnection[],
  sfVariantByTargetId: Map<string, SalesforceMatch>
): SalesforceMatch | undefined => {
  for (const connection of connections) {
    if (!connection.targetVariantId) continue;
    const match = sfVariantByTargetId.get(connection.targetVariantId);
    if (match) return match;
  }
  return undefined;
};

const applySalesforceOverlay = (target: FormVariant, match: SalesforceMatch | undefined): void => {
  if (!match) return;
  const { variant: sf, product: sfProduct } = match;
  // SF status goes onto the variant for downstream filters (resolveWebStatus).
  // PIM-owned fields are intentionally left alone when present.
  if (sfProduct.status !== undefined) target.status = sfProduct.status;
  if (sf.price !== undefined && (target.price === undefined || target.price === "")) {
    target.price = sf.price;
  }
  if (sf.wholesalePrice !== undefined && (target.wholesalePrice === undefined || target.wholesalePrice === "")) {
    target.wholesalePrice = sf.wholesalePrice;
  }
  if (sf.webPrice !== undefined && target.webPrice === undefined) {
    target.webPrice = sf.webPrice;
  }
  if (sf.startDate !== undefined) target.startDate = sf.startDate;
  if (sf.endDate !== undefined) target.endDate = sf.endDate;
  target.term ??= sf.term;
  if (sf.maxBagsShippedPerWeek !== undefined) {
    target.maxBagsShippedPerWeek = sf.maxBagsShippedPerWeek;
    target.weeklyInventory ??= sf.maxBagsShippedPerWeek;
  }
  if (sf.inventoryQuantity !== undefined) {
    target.totalMaxShortTermInventory = sf.inventoryQuantity;
  }
  target.isImportedRemote = true;
};

/**
 * Two orthogonal things happen here — keep them separate.
 *
 * 1. `isImportedLocalVariant` is a *source-of-truth* flag: "this row is
 *    backed by a Shopify variant." The table's `createVariantWithProductData`
 *    reads it to pick the "Shopify" vs "Manual" source badge. For connected
 *    variants, the flag should flip the moment we see an ACTIVE connection
 *    with a `targetVariantId` — regardless of whether the installation fetch
 *    has populated `remoteProducts` yet.
 *
 * 2. `inventoryQuantity` is the *live-inventory overlay*: "this is the
 *    current Shopify stock sum across all connections for this PIM SKU."
 *    It depends on `remoteProducts` being loaded. If the remote fetch is
 *    late or errors, we leave `inventoryQuantity` untouched so the PIM
 *    value (or `undefined`) shows through on the first paint, then fills
 *    in on a later render once `remoteProducts` arrives.
 *
 * Previously both assignments sat inside `if (summed !== undefined)`, which
 * meant a late/failed `remoteProducts` fetch caused connected rows to be
 * badged "Manual" until inventory resolved. Splitting the two conditions
 * fixes that regression.
 */
const applyInventoryOverlay = (target: FormVariant, connections: ProductConnection[], remoteVariantById: Map<string, Variant>): void => {
  let summed: number | undefined;
  let hasShopifyBackedConnection = false;
  for (const connection of connections) {
    if (!connection.targetVariantId) continue;
    // A connection with a `targetVariantId` is enough to know this row is
    // Shopify-backed — the remote lookup below only decides whether we
    // *also* have a current stock number to overlay.
    hasShopifyBackedConnection = true;
    const remote = remoteVariantById.get(connection.targetVariantId);
    if (!remote || typeof remote.inventoryQuantity !== "number") continue;
    summed = (summed ?? 0) + remote.inventoryQuantity;
  }
  if (hasShopifyBackedConnection) {
    target.isImportedLocalVariant = true;
  }
  if (summed !== undefined) {
    target.inventoryQuantity = summed;
  }
};

/**
 * Drops variants whose Salesforce `Approval_status__c` is one of the hidden
 * onboarding statuses (Draft / Submitted for Approval / Copywrite-*), and
 * drops products left with zero variants. Two classes of variant are kept:
 *
 * - No Salesforce status (plain Shopify/PIM installation rows).
 * - A PIM `webStatus` that takes precedence over a stale Salesforce status —
 *   the SS merge path copies the SF product status onto every variant via
 *   `applyFallbackStatus`, so a SKU that is already Visible/Not Visible/Out
 *   of Stock in PIM must not disappear just because SF still says Draft or
 *   Rejected. Mirrors `resolveWebStatus`'s "webStatus wins" semantics.
 * - A Salesforce "Rejected" status: an explicit manager rejection must remain
 *   visible to the vendor. resolveWebStatus maps it to webStatus "Rejected"
 *   (red badge) downstream.
 */
export function filterOutHiddenSalesforceStatuses(products: FormProduct[]): FormProduct[] {
  return products
    .map((product) => ({
      ...product,
      variants: product.variants.filter((variant) => {
        if (!variant.status) return true;
        if (variant.webStatus) return true;
        return !isHiddenSalesforceStatus(variant.status);
      }),
    }))
    .filter((product) => product.variants.length > 0);
}

/**
 * Isolates genuinely pre-PIM Rejected Salesforce records — the "Rejected
 * Products" list surfaced from the SS SKU selection modal. A variant with a
 * PIM `webStatus` is already a live catalog row (PIM `webStatus` always wins,
 * per `filterOutHiddenSalesforceStatuses`), so it's excluded here even if its
 * Salesforce status happens to read "Rejected".
 *
 * Products without a `serviceId` are dropped: resubmitting updates the
 * existing Salesforce record by its Id, and without one the onboarding modal
 * silently falls back to create mode — minting a duplicate request instead.
 */
export function filterRejectedSalesforceProducts(products: FormProduct[]): FormProduct[] {
  return products
    .filter((product) => Boolean(product.serviceId))
    .map((product) => ({
      ...product,
      variants: product.variants.filter((variant) => variant.status === REJECTED_STATUS && !variant.webStatus),
    }))
    .filter((product) => product.variants.length > 0);
}
